import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { dto } from '@photo-vault/shared';
import { Card, Icon, PageHeader } from '../ui/index.js';
import { useAuthSession } from '../auth/AuthContext.js';
import { useInfra } from '../infra/context.js';

export function Home() {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [bucket, setBucket] = useState<dto.BucketStatusResponse | 'loading' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = metadata.fetchBucketStatus;
    if (!fetchStatus) {
      setBucket('error');
      return;
    }
    fetchStatus(session.jwt)
      .then((status) => {
        if (!cancelled) setBucket(status);
      })
      .catch(() => {
        if (!cancelled) setBucket('error');
      });
    return () => {
      cancelled = true;
    };
  }, [session.jwt, metadata]);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="vault sbloccato"
        title={
          <>
            Bentornato, <span className="accent-text break-all">{session.email.split('@')[0]}</span>
          </>
        }
        description="La tua chiave master vive solo in questa scheda. Niente lascia il dispositivo in chiaro."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="flex flex-col gap-2">
          <Icon name="encrypted" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Identità cifrata</h3>
          <p className="text-sm text-on-surface-variant">
            User ID <span className="font-display text-cyan">{session.userId.slice(0, 8)}…</span>
          </p>
        </Card>
        <Card className="flex flex-col gap-2">
          <Icon name="vpn_key" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">RSA pubblica</h3>
          <p className="break-all font-display text-xs text-on-surface-variant">
            {session.rsaPubB64.slice(0, 40)}…
          </p>
        </Card>
        <BucketCard state={bucket} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <ActionCard
          icon="folder_open"
          title="I tuoi album"
          description="Crea album radice e sotto-album. Ogni radice ha la sua chiave AES-256, generata sul tuo device."
          to="/albums"
          cta="Apri album"
          ctaIcon="arrow_forward"
          primary
        />
        <ActionCard
          icon="folder_shared"
          title="Condivisi con me"
          description="Album che altri hanno condiviso con te. La chiave è cifrata per la tua chiave pubblica."
          to="/shared"
          cta="Apri condivisi"
          ctaIcon="arrow_forward"
        />
        <ActionCard
          icon="lock_reset"
          title="Sicurezza"
          description="Cambia la passphrase senza ruotare la coppia RSA — album e share restano accessibili."
          to="/settings/passphrase"
          cta="Cambia passphrase"
          ctaIcon="arrow_forward"
        />
        <ActionCard
          icon="password"
          title="Codice di recupero"
          description="Genera un nuovo codice se hai perso il precedente. È l'unico modo per rientrare se dimentichi la passphrase."
          to="/settings/recovery-code"
          cta="Gestisci codice"
          ctaIcon="arrow_forward"
        />
      </div>
    </div>
  );
}

function ActionCard({
  icon,
  title,
  description,
  to,
  cta,
  ctaIcon,
  primary = false,
}: {
  icon: string;
  title: string;
  description: string;
  to: string;
  cta: string;
  ctaIcon: string;
  primary?: boolean;
}) {
  return (
    <Link to={to} className="group block">
      <Card className="flex h-full flex-col gap-4 transition-all hover:border-on-surface-variant/40 hover:shadow-elevated">
        <span
          className={
            primary
              ? 'flex h-11 w-11 items-center justify-center rounded-xl bg-cyan/10 text-cyan'
              : 'flex h-11 w-11 items-center justify-center rounded-xl border border-outline bg-surface-container-low text-on-surface-variant'
          }
        >
          <Icon name={icon} className="text-xl" />
        </span>
        <div className="flex flex-col gap-1.5">
          <h3 className="font-display text-lg font-semibold leading-tight">{title}</h3>
          <p className="text-sm leading-relaxed text-on-surface-variant">{description}</p>
        </div>
        <span className="mt-auto inline-flex items-center gap-1.5 pt-1 text-sm font-semibold text-cyan">
          {cta}
          <Icon
            name={ctaIcon}
            className="text-base transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </Card>
    </Link>
  );
}

function BucketCard({ state }: { state: dto.BucketStatusResponse | 'loading' | 'error' }) {
  const connected = typeof state === 'object' && state.connected;

  return (
    <Card className="flex flex-col gap-2">
      <Icon
        name={connected ? 'cloud_done' : 'cloud_off'}
        className={connected ? 'text-2xl text-cyan' : 'text-2xl text-on-surface-variant'}
      />
      <h3 className="font-display text-lg font-semibold">Bucket personale</h3>
      {state === 'loading' ? (
        <p className="text-sm text-on-surface-variant">Verifico…</p>
      ) : state === 'error' ? (
        <p className="text-sm text-on-surface-variant">Stato non disponibile.</p>
      ) : state.connected ? (
        <>
          <p className="break-all text-sm text-on-surface-variant">
            <span className="font-display text-cyan">{state.bucketName}</span>
            <br />
            <span className="text-xs">{state.region}</span>
          </p>
          <Link
            to="/settings/bucket"
            className="inline-flex items-center gap-1 self-start text-xs font-semibold uppercase tracking-[0.08em] text-on-surface-variant transition-colors hover:text-cyan"
          >
            Gestisci
            <Icon name="arrow_forward" className="text-sm" />
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-on-surface-variant">
            Non ancora collegato. Bastano un account AWS e 12 cifre.
          </p>
          <Link
            to="/settings/bucket"
            className="inline-flex items-center gap-2 self-start rounded-lg border border-cyan/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-cyan transition-colors hover:border-cyan"
          >
            <Icon name="link" className="text-sm" />
            Collega bucket
          </Link>
        </>
      )}
    </Card>
  );
}
